import { useForm } from 'react-hook-form'

import { useMyMonitoringAddMyMonitoringUser } from '@apps-common/apis/my-monitoring/users'
import { useCreateClientAccount } from '@apps-common/apis/portal/client-accounts'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { CheckboxInputSM, InputInfoSM, InputSM } from '@apps-common/components/elements/form'
import { ModalBody, ModalFooter, ModalTopConfirmActionButton } from '@apps-common/components/modals/modal'
import { MESSAGE_IS_MM_ADMIN } from '@apps-common/config/app-messages'
import { ModalDismiss } from '@apps-common/config/modal'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { INewClientAccountModel } from '@apps-common/types/portal/client-account'

interface IProps {
  uId: string
  email: string
  closeCb: (role: string) => void
  resetCb: () => void
}

export const MyMonitoringUserAddNewForm: React.FC<IProps> = ({ uId, email, closeCb, resetCb }) => {
  const userCreateUserHook = useProjectHook(useCreateClientAccount, useMyMonitoringAddMyMonitoringUser)

  const { mutate: create, isPending: createIsPending } = userCreateUserHook(true, () => closeCb(ModalDismiss.Confirm))

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<INewClientAccountModel>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email,
      phone: '',
      isAdmin: false,
      unitAssociation: {
        unitId: uId,
        // accountNo: '',
        // siteName: '',
        userNumber: '',
        mapPartition: false,
        userPartition: ''
      }
    }
  })

  const submit = (values: INewClientAccountModel) => {
    if (values?.unitAssociation?.userPartition && values?.unitAssociation?.userPartition?.length > 0) {
      values.unitAssociation.mapPartition = true
    }

    create({ model: values })
  }

  const isLoading = isSubmitting || createIsPending

  return (
    <>
      <ModalBody classes='!pt-[95px]'>
        <div className='w-full flex items-center justify-end'>
          <ModalTopConfirmActionButton
            loading={isLoading}
            confirmCallback={resetCb}
            text='Change email'
            icon='arrowAngledLeft'
            classes='action-warning'
          />
        </div>
        <form id='my-monitoring-user-add-form' noValidate onSubmit={handleSubmit(submit)}>
          <InputInfoSM id='userEmail' label='Email' showLabel value={email} />
          <div className='grid sm:grid-cols-2 gap-[10px]'>
            <InputSM
              key='userFirstName'
              type='text'
              placeholder='First name'
              label='First name'
              id='userFirstName'
              showLabel
              disabled={isLoading}
              registration={register('firstName', {
                required: true,
                maxLength: {
                  message: 'No longer than 15 characters',
                  value: 15
                }
              })}
              errors={errors.firstName}
            />
            <InputSM
              key='userLastName'
              type='text'
              placeholder='Last name'
              label='Last name'
              id='userLastName'
              showLabel
              disabled={isLoading}
              registration={register('lastName', {
                required: true,
                maxLength: {
                  message: 'No longer than 15 characters',
                  value: 15
                }
              })}
              errors={errors.lastName}
            />
          </div>
          <InputSM
            key='userNumber'
            type='text'
            placeholder='1 to 999'
            label='User number (Zone)'
            id='userNumber'
            showLabel
            disabled={isLoading}
            registration={register('unitAssociation.userNumber', {
              required: true,
              pattern: {
                value: /^[0-9]{3}$/,
                message: '3 numerical characters only e.g. 001'
              }
            })}
            errors={errors.unitAssociation?.userNumber}
          />
          <InputSM
            key='userPartition'
            type='text'
            placeholder='0 to 99'
            label='User partition (If required)'
            id='userPartition'
            showLabel
            disabled={isLoading}
            registration={register('unitAssociation.userPartition', {
              required: false,
              pattern: {
                value: /^[0-9]{2}$/,
                message: '2 numerical characters only e.g. 01'
              }
            })}
            errors={errors.unitAssociation?.userPartition}
          />
          <CheckboxInputSM
            label='Site administrator'
            id='isAdmin'
            disabled={isLoading}
            registration={register('isAdmin')}
            errors={errors.isAdmin}
            info={MESSAGE_IS_MM_ADMIN}
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-between gap-2'>
          <ButtonRoundedSm type='submit' form='my-monitoring-user-add-form' loading={isLoading} text='Create' className='action-success' />
          <ButtonRoundedSm
            type='button'
            loading={isLoading}
            text='Cancel'
            className='action-neutral'
            callback={() => closeCb(ModalDismiss.Cancel)}
          />
        </div>
      </ModalFooter>
    </>
  )
}
